import { useQuery } from '@apollo/client';
import { createError } from '@common/neo.tax';

import {
  Conditions,
  GET_ASC_PROJECTS_SUMMARY,
  GetAscProjectsSummaryQuery,
  GetAscProjectsSummaryQueryVariables,
  keywordFilter,
  useCurrentCompanyId,
  useCurrentCompanyOrFail,
  useSticky,
} from '@neo.tax/shared';

import { useAscContext } from '../context';
import { AscProjectFilterableColumn } from '../types';
import { mapAscProjectsSummaryFilterConditions } from '../utils';

export type AscProjectsSummary = NonNullable<GetAscProjectsSummaryQuery['getAscProjectsSummary']>;

type UseAscProjectsSummaryProps = {
  filterConditions?: Conditions<AscProjectFilterableColumn>;
  searchKeyword?: string | null;
};

type UseAscProjectsSummaryResult =
  | {
      loading: true;
      ascProjectsSummary: null;
    }
  | {
      loading: false;
      ascProjectsSummary: AscProjectsSummary;
    };

export const useAscProjectsSummary = ({
  filterConditions,
  searchKeyword,
}: UseAscProjectsSummaryProps = {}): UseAscProjectsSummaryResult => {
  const companyId = useCurrentCompanyId();
  const { lastCompletedAscSyncPeriodEnd } = useCurrentCompanyOrFail();
  const { fiscalYearId, periodName } = useAscContext();

  const {
    data: requestData,
    error,
    loading,
  } = useQuery<GetAscProjectsSummaryQuery, GetAscProjectsSummaryQueryVariables>(GET_ASC_PROJECTS_SUMMARY, {
    variables: {
      companyId,
      fiscalYearId,
      periodName,
      filter: {
        and: [
          keywordFilter(['name'], searchKeyword),
          mapAscProjectsSummaryFilterConditions(filterConditions, lastCompletedAscSyncPeriodEnd),
        ],
      },
    },
  });

  // Keep existing data on the screen while a new query is running, so it
  // doesn't keep clearing the screen as you type into the filter.
  const stickyData = useSticky(requestData);
  const ascProjectsSummary = stickyData?.getAscProjectsSummary ?? null;

  if (loading) {
    return {
      loading: true,
      ascProjectsSummary: null,
    };
  }
  if (error) {
    throw error;
  }

  if (!stickyData || !ascProjectsSummary) {
    throw createError('MissingAscProjectsSummary', 'No data returned from getAscProjectsSummary query');
  }

  return {
    loading,
    ascProjectsSummary,
  };
};
